import { Body } from 'app/components/Typography';
import { LiveChatItem } from 'app/hooks/fetch/useFetchLiveChat';
import useHTMLLinkClickHandler from 'app/hooks/useHTMLLinkClickHandler';
import { linkify } from 'app/utils/linkify';
import { FormEventHandler, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatModuleType, LiveChatModule } from 'app/copied/models/ChatModules';
import useModule from 'app/hooks/useModule';
import { getButtonClass } from 'app/utils/getButtonClass';
import useInputFontSizeClass from 'app/hooks/useInputFontSizeClass';
import { cx } from '@twind/core';

import useLiveChatTracking from '../../hooks/useLiveChatTracking';

type Props = {
  form: LiveChatItem;
  onSubmit: (
    data: {
      [k: string]: FormDataEntryValue;
    },
    form: LiveChatItem
  ) => void;
  isLoading?: boolean;
  onSkip?: (
    data: {
      [k: string]: FormDataEntryValue;
    },
    form: LiveChatItem
  ) => void;
};

const LiveChatMessageForm = ({ form, onSubmit, onSkip, isLoading }: Props) => {
  const { t } = useTranslation();
  const fontSizeClass = useInputFontSizeClass();
  const { captureContactFormRequested } = useLiveChatTracking();
  const liveChatModule = useModule(ChatModuleType.LiveChat) as unknown as LiveChatModule;
  const handleHTMLLinkClick = useHTMLLinkClickHandler();
  const [answer, setAnswer] = useState('');

  const isSingleChoice = useMemo(() => {
    return form?.items?.some((it: any) => it?.choices?.length > 0); // eslint-disable-line @typescript-eslint/no-explicit-any
  }, [form.items]);

  const isContactForm = useMemo(() => {
    return form?.items?.some((it) => it.ref_id === 'contact-info' || it.ref_id.startsWith('collect-info'));
  }, [form.items]);

  useEffect(() => {
    if (isContactForm) {
      captureContactFormRequested();
    }
  }, [isContactForm, captureContactFormRequested]);

  if (!form) return null;

  const { dismissed, completed, dismissable } = form;

  const showSubmitButton = !dismissed && !completed && !isSingleChoice;
  const showCancelButton = !dismissed && !completed && dismissable && !isSingleChoice;

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const formData = new FormData(e.target as HTMLFormElement);
    const json = Object.fromEntries(formData.entries());

    e.type === 'submit' ? onSubmit?.(json, form) : onSkip?.(json, form);
  };

  return (
    <form
      data-testid="live-chat-message-form"
      className="flex flex-col gap-4 mb-0"
      onSubmit={handleSubmit}
      onReset={handleSubmit}
    >
      <input
        type="hidden"
        name="form_ref_id"
        value={form.ref_id}
      />

      {form.items?.map((it, index) => {
        if (it.item_type === 'text') {
          return (
            <div
              key={index}
              className="[&_p]:(mb-2 empty:pb-[1em] last-of-type:mb-0)"
              onClick={handleHTMLLinkClick}
              dangerouslySetInnerHTML={{
                __html: linkify(it.params?.label),
              }}
            />
          );
        }
        if (it.item_type === 'checkbox') {
          return (
            <div
              key={index}
              className="[&_p]:(mb-2 empty:pb-[1em] last-of-type:mb-0)"
              onClick={handleHTMLLinkClick}
              dangerouslySetInnerHTML={{
                __html: linkify(it.params?.label),
              }}
            />
          );
        }
        if (it.item_type === 'input') {
          return (
            <Fragment key={index}>
              {it.params?.label && (
                <div
                  className="[&_p]:(mb-2 empty:pb-[1em] last-of-type:mb-0)"
                  onClick={handleHTMLLinkClick}
                  dangerouslySetInnerHTML={{
                    __html: linkify(it.params?.label),
                  }}
                />
              )}

              {form.dismissed || form.completed ? (
                <p key={index}>
                  <Answer value={it.value} />
                </p>
              ) : null}

              {!form.dismissed && !form.completed && (
                <>
                  <input
                    type="hidden"
                    name="item_ref_id"
                    value={it.ref_id}
                  />
                  <input
                    className={cx(
                      'px-5 py-3 h-[56px] md:text-sm rounded-2xl bg-white border border-[#aeaeae] focus:outline focus:outline-brand-primary outline-2',
                      fontSizeClass
                    )}
                    value={answer}
                    onChange={({ target: { value } }) => {
                      setAnswer(value);
                    }}
                    name="value"
                    required={it.required}
                    placeholder={it.params?.placeholder}
                  />

                  {it.errors?.length
                    ? it.errors.map((error: string) => (
                        <Body
                          key={error}
                          className="text-error mb-2"
                        >
                          {error}
                        </Body>
                      ))
                    : null}
                </>
              )}
            </Fragment>
          );
        }
        if (it.item_type === 'single_choice') {
          return null; //<SingleChoice key={index} formItem={it} typeChat="chat" />;
        }
        return null;
      })}

      {showSubmitButton && (
        <button
          disabled={isLoading}
          type="submit"
          className={getButtonClass(liveChatModule?.button_type)}
        >
          {t('form_submit')}
        </button>
      )}

      {showCancelButton && (
        <button
          type="reset"
          disabled={isLoading}
          className="underline font-normal text-sm disabled:opacity-50"
        >
          {t('form_skip')}
        </button>
      )}
    </form>
  );
};

function Answer({ value }: { value?: string | boolean | null }) {
  const { t } = useTranslation();

  if (!value) {
    return <>{t('skipped')}</>;
  }

  return (
    <>
      {t('contact_details')}:
      <br />
      <i>{value}</i>
    </>
  );
}

export default LiveChatMessageForm;
